/**
 * Repository layer over IndexedDB.
 *
 * Every function takes a `userId` and SCOPES every query to that user.
 * Per-user isolation is enforced here — the caller cannot accidentally
 * read or write another user's data through this API.
 *
 * Writes propagate errors. If IndexedDB throws (quota exceeded, broken
 * connection, etc.), the error bubbles up to the caller. The repository
 * never silently swallows failures — that would lose the user's data.
 */

import { getDb } from './db';
import type {
  CheckState,
  CheckStatus,
  DbMetadata,
  DefectInstance,
  Property,
  PropertyType,
  Room,
  RoomType,
  User,
} from './types';

const now = (): number => Date.now();

/**
 * UUID v4 generator. Prefers `crypto.randomUUID()` when available
 * (browsers, modern Node) and falls back to a manual scheme for
 * very old environments. Not security-sensitive.
 */
export function newId(): string {
  if (
    typeof globalThis.crypto !== 'undefined' &&
    typeof globalThis.crypto.randomUUID === 'function'
  ) {
    return globalThis.crypto.randomUUID();
  }
  // Fallback — RFC 4122 v4 from Math.random. Adequate for non-secure IDs.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// ---------- Users ----------

/**
 * Get or create the singleton anonymous user record for this device.
 * Called on first app open. Returns the existing record on subsequent
 * opens.
 */
export async function getOrCreateAnonymousUser(): Promise<User> {
  const db = await getDb();
  const existing = await db.get('metadata', 'global');
  if (existing) {
    const user = await db.get('users', existing.anonymousUserId);
    if (user) return user;
    // Metadata pointed at a missing user — recover by creating fresh.
  }
  const user: User = {
    id: newId(),
    isAnonymous: true,
    email: null,
    remoteId: null,
    createdAt: now(),
    updatedAt: now(),
  };
  const meta: DbMetadata = {
    id: 'global',
    schemaVersion: 1,
    lastSyncedAt: null,
    anonymousUserId: user.id,
  };
  const tx = db.transaction(['users', 'metadata'], 'readwrite');
  await tx.objectStore('users').add(user);
  await tx.objectStore('metadata').put(meta);
  await tx.done;
  return user;
}

export async function getUser(userId: string): Promise<User | undefined> {
  const db = await getDb();
  return db.get('users', userId);
}

/**
 * Upgrade an anonymous user to a real account in-place. This is the
 * core of the brief's "anonymous → account merge with zero data loss"
 * requirement — properties, rooms, check states, and defects keep
 * pointing at the same userId.
 */
export async function upgradeUserToAccount(
  userId: string,
  email: string,
  remoteId: string,
): Promise<User> {
  const db = await getDb();
  const tx = db.transaction('users', 'readwrite');
  const existing = await tx.store.get(userId);
  if (!existing) {
    throw new Error(`Cannot upgrade missing user ${userId}`);
  }
  const upgraded: User = {
    ...existing,
    isAnonymous: false,
    email,
    remoteId,
    updatedAt: now(),
  };
  await tx.store.put(upgraded);
  await tx.done;
  return upgraded;
}

/**
 * Sign-out: create a brand-new anonymous user and repoint the device's
 * "current user" pointer at it. The previous (signed-in) account's local
 * records are left untouched but are no longer the active user, so they
 * disappear from the UI — the account itself lives on the server and
 * re-syncs on the next sign-in. (Full local wipe is a separate concern,
 * handled by the PDPA delete-account flow.)
 */
export async function resetToFreshAnonymousUser(): Promise<User> {
  const db = await getDb();
  const existingMeta = await db.get('metadata', 'global');
  const user: User = {
    id: newId(),
    isAnonymous: true,
    email: null,
    remoteId: null,
    createdAt: now(),
    updatedAt: now(),
  };
  const meta: DbMetadata = {
    id: 'global',
    schemaVersion: existingMeta?.schemaVersion ?? 1,
    lastSyncedAt: null,
    anonymousUserId: user.id,
  };
  const tx = db.transaction(['users', 'metadata'], 'readwrite');
  await tx.objectStore('users').add(user);
  await tx.objectStore('metadata').put(meta);
  await tx.done;
  return user;
}

// ---------- Properties ----------

export interface CreatePropertyInput {
  userId: string;
  label: string;
  addressLine?: string | null;
  postalCode?: string | null;
  propertyType: PropertyType;
}

export async function createProperty(
  input: CreatePropertyInput,
): Promise<Property> {
  const db = await getDb();
  const property: Property = {
    id: newId(),
    userId: input.userId,
    label: input.label,
    addressLine: input.addressLine ?? null,
    postalCode: input.postalCode ?? null,
    propertyType: input.propertyType,
    createdAt: now(),
    updatedAt: now(),
  };
  await db.add('properties', property);
  return property;
}

export async function listPropertiesForUser(
  userId: string,
): Promise<Property[]> {
  const db = await getDb();
  return db.getAllFromIndex('properties', 'by-user', userId);
}

export async function getProperty(
  userId: string,
  propertyId: string,
): Promise<Property | undefined> {
  const db = await getDb();
  const prop = await db.get('properties', propertyId);
  if (!prop || prop.userId !== userId) return undefined;
  return prop;
}

export async function deleteProperty(
  userId: string,
  propertyId: string,
): Promise<void> {
  const db = await getDb();
  const tx = db.transaction(
    ['properties', 'rooms', 'checkStates', 'defectInstances'],
    'readwrite',
  );
  const prop = await tx.objectStore('properties').get(propertyId);
  if (!prop || prop.userId !== userId) {
    await tx.done;
    return; // cannot delete a property that isn't yours
  }
  // Cascade: delete rooms, check states, defect instances for this property.
  const rooms = await tx
    .objectStore('rooms')
    .index('by-property')
    .getAll(propertyId);
  for (const room of rooms) {
    await tx.objectStore('rooms').delete(room.id);
    const checks = await tx
      .objectStore('checkStates')
      .index('by-room')
      .getAll(room.id);
    for (const cs of checks) {
      await tx.objectStore('checkStates').delete(cs.id);
      const defects = await tx
        .objectStore('defectInstances')
        .index('by-check-state')
        .getAll(cs.id);
      for (const d of defects) {
        await tx.objectStore('defectInstances').delete(d.id);
      }
    }
  }
  await tx.objectStore('properties').delete(propertyId);
  await tx.done;
}

// ---------- Rooms ----------

export interface CreateRoomInput {
  userId: string;
  propertyId: string;
  label: string;
  roomType: RoomType;
  position: number;
}

export async function createRoom(input: CreateRoomInput): Promise<Room> {
  const db = await getDb();
  // Defence in depth: verify property is owned by this user before adding a room.
  const prop = await db.get('properties', input.propertyId);
  if (!prop || prop.userId !== input.userId) {
    throw new Error('Property not found for this user.');
  }
  const room: Room = {
    id: newId(),
    propertyId: input.propertyId,
    userId: input.userId,
    label: input.label,
    roomType: input.roomType,
    position: input.position,
    createdAt: now(),
    updatedAt: now(),
  };
  await db.add('rooms', room);
  return room;
}

export async function listRoomsForProperty(
  userId: string,
  propertyId: string,
): Promise<Room[]> {
  const db = await getDb();
  const rooms = await db.getAllFromIndex('rooms', 'by-property', propertyId);
  return rooms
    .filter((r) => r.userId === userId)
    .sort((a, b) => a.position - b.position);
}

// ---------- Check states ----------

export interface SetCheckStatusInput {
  userId: string;
  propertyId: string;
  roomId: string;
  checkId: string;
  status: CheckStatus;
  note?: string | null;
}

/**
 * Upsert the user's status on a check. There's at most one CheckState
 * per (roomId, checkId) pair — repeated calls update in place.
 */
export async function setCheckStatus(
  input: SetCheckStatusInput,
): Promise<CheckState> {
  const db = await getDb();
  const tx = db.transaction(['rooms', 'checkStates'], 'readwrite');
  const room = await tx.objectStore('rooms').get(input.roomId);
  if (!room || room.userId !== input.userId) {
    await tx.done;
    throw new Error('Room not found for this user.');
  }
  // Find existing CheckState for this (roomId, checkId).
  const existing = (
    await tx.objectStore('checkStates').index('by-room').getAll(input.roomId)
  ).find((cs) => cs.checkId === input.checkId);
  const record: CheckState = existing
    ? {
        ...existing,
        status: input.status,
        note: input.note ?? existing.note,
        updatedAt: now(),
      }
    : {
        id: newId(),
        userId: input.userId,
        propertyId: input.propertyId,
        roomId: input.roomId,
        checkId: input.checkId,
        status: input.status,
        note: input.note ?? null,
        createdAt: now(),
        updatedAt: now(),
      };
  await tx.objectStore('checkStates').put(record);
  await tx.done;
  return record;
}

export async function listCheckStatesForRoom(
  userId: string,
  roomId: string,
): Promise<CheckState[]> {
  const db = await getDb();
  const states = await db.getAllFromIndex('checkStates', 'by-room', roomId);
  return states.filter((s) => s.userId === userId);
}

// ---------- Defect instances ----------

export interface AddDefectInput {
  userId: string;
  checkStateId: string;
  defectType: string;
  measurements?: Record<string, number>;
  locationDetail?: string;
  notes?: string;
}

export async function addDefectInstance(
  input: AddDefectInput,
): Promise<DefectInstance> {
  const db = await getDb();
  // Defence in depth: verify check state belongs to this user before adding.
  const cs = await db.get('checkStates', input.checkStateId);
  if (!cs || cs.userId !== input.userId) {
    throw new Error('Check state not found for this user.');
  }
  const defect: DefectInstance = {
    id: newId(),
    userId: input.userId,
    checkStateId: input.checkStateId,
    defectType: input.defectType,
    measurements: input.measurements ?? {},
    locationDetail: input.locationDetail ?? '',
    notes: input.notes ?? '',
    createdAt: now(),
    updatedAt: now(),
  };
  await db.add('defectInstances', defect);
  return defect;
}

export async function listDefectsForCheckState(
  userId: string,
  checkStateId: string,
): Promise<DefectInstance[]> {
  const db = await getDb();
  const defects = await db.getAllFromIndex(
    'defectInstances',
    'by-check-state',
    checkStateId,
  );
  return defects.filter((d) => d.userId === userId);
}

// ---------- Cross-cutting ----------

/** Total count of defects across a user's whole dataset. Useful for the dashboard. */
export async function countDefectsForUser(userId: string): Promise<number> {
  const db = await getDb();
  const all = await db.getAllFromIndex('defectInstances', 'by-user', userId);
  return all.length;
}

/** Total count of properties for a user. */
export async function countPropertiesForUser(userId: string): Promise<number> {
  const db = await getDb();
  return db.countFromIndex('properties', 'by-user', userId);
}
